/**
 * The one fetch wrapper. JSON in, JSON out, the build in `X-Ugis-Build`
 * (events.md §8.4), and errors as values the screens can read: a status and
 * the API's `detail`, which is either a sentence or `{need: "picker"}`.
 */
export const BUILD: string = (import.meta.env?.VITE_UGIS_BUILD as string | undefined) ?? "dev";

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: unknown,
  ) {
    super(typeof detail === "string" ? detail : JSON.stringify(detail));
  }

  /** `{need: "picker" | "sitting" | "reassert"}` from a 409 or 403. */
  get need(): string | undefined {
    const d = this.detail as { need?: unknown } | null;
    return d && typeof d === "object" && typeof d.need === "string" ? d.need : undefined;
  }
}

/** A network failure (no response at all). The queue and the answer retry on it. */
export class NetworkError extends Error {}

export async function api<T>(method: "GET" | "POST", path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: "same-origin",
      headers: {
        "X-Ugis-Build": BUILD,
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    throw new NetworkError(String(e));
  }
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const detail = data && typeof data === "object" && "detail" in data ? (data as { detail: unknown }).detail : data;
    throw new ApiError(res.status, detail);
  }
  return data as T;
}

export const get = <T>(path: string) => api<T>("GET", path);
export const post = <T>(path: string, body: unknown = {}) => api<T>("POST", path, body);
